import { apiService } from './api';

export interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  role: 'client' | 'lawyer' | 'admin';
  emailVerified: boolean;
  phoneVerified: boolean;
  profilePicture?: string;
  city?: string;
  address?: string;
  dateOfBirth?: string;
  gender?: 'male' | 'female' | 'other';
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface SignupData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phone: string;
  city?: string;
}

export interface AuthResponse {
  user: User;
  token: string;
  refreshToken: string;
}

class AuthService {
  // User Signup
  async signup(signupData: SignupData): Promise<AuthResponse> {
    const response = await apiService.post<AuthResponse>('/auth/signup', signupData);
    
    if (response.success && response.data) {
      this.setAuthToken(response.data.token);
      this.setRefreshToken(response.data.refreshToken);
      this.setUser(response.data.user);
    }
    
    return response.data!;
  }

  // User Login
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await apiService.post<AuthResponse>('/auth/login', credentials);
    
    if (response.success && response.data) {
      this.setAuthToken(response.data.token);
      this.setRefreshToken(response.data.refreshToken);
      this.setUser(response.data.user);
    }
    
    return response.data!;
  }

  // User Logout
  async logout(): Promise<void> {
    try {
      await apiService.post('/auth/logout', {});
    } finally {
      this.clearAuthData();
    }
  }

  // Refresh Token
  async refreshToken(): Promise<string> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await apiService.post<{ token: string; refreshToken: string }>('/auth/refresh-token', {
      refreshToken
    });

    if (response.success && response.data) {
      this.setAuthToken(response.data.token);
      this.setRefreshToken(response.data.refreshToken);
      return response.data.token;
    }

    throw new Error('Token refresh failed');
  }

  // Forgot Password
  async forgotPassword(email: string): Promise<void> {
    await apiService.post('/auth/forgot-password', { email });
  }

  // Reset Password
  async resetPassword(token: string, newPassword: string): Promise<void> {
    await apiService.post('/auth/reset-password', { token, newPassword });
  }

  // Token Management - Enhanced Security
  // Note: localStorage is used for demo. Production should use httpOnly cookies
  setAuthToken(token: string): void {
    // Consider implementing encryption for tokens in production
    sessionStorage.setItem('authToken', token);
  }

  getAuthToken(): string | null {
    return sessionStorage.getItem('authToken');
  }

  setRefreshToken(token: string): void {
    // Refresh tokens should be httpOnly cookies in production
    sessionStorage.setItem('refreshToken', token);
  }

  getRefreshToken(): string | null {
    return sessionStorage.getItem('refreshToken');
  }

  setUser(user: User): void {
    // Store user data (email and phone are needed for UI display)
    const userData = {
      id: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      phone: user.phone,
      role: user.role,
      emailVerified: user.emailVerified,
      phoneVerified: user.phoneVerified
    };
    sessionStorage.setItem('currentUser', JSON.stringify(userData));
  }

  getCurrentUser(): User | null {
    const userStr = sessionStorage.getItem('currentUser');
    if (!userStr) return null;
    
    try {
      return JSON.parse(userStr);
    } catch (error) {
      console.error('Error parsing user data from storage');
      this.clearAuthData();
      return null;
    }
  }

  isAuthenticated(): boolean {
    const token = this.getAuthToken();
    if (!token) return false;
    
    // Basic token format validation
    const parts = token.split('.');
    return parts.length === 3;
  }

  clearAuthData(): void {
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('currentUser');
    localStorage.removeItem('authToken'); // Clear legacy localStorage tokens
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('currentUser');
  }

  // Auto-refresh token when it expires
  async setupTokenRefresh(): Promise<void> {
    const token = this.getAuthToken();
    if (!token) return;

    try {
      // Decode JWT to check expiry (basic implementation)
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiryTime = payload.exp * 1000;
      const currentTime = Date.now();
      const timeUntilExpiry = expiryTime - currentTime;

      // Refresh token 5 minutes before expiry
      if (timeUntilExpiry > 0 && timeUntilExpiry < 5 * 60 * 1000) {
        await this.refreshToken();
      }
    } catch (error) {
      console.error('Token refresh setup error:', error);
    }
  }
}

export const authService = new AuthService();